import * as E from 'fp-ts/Either';
import { Box, Button, Flex, LoadingOverlay } from '@mantine/core';
import { useAuth } from '../auth/AuthContext';
import { ReactNode, useEffect, useState } from 'react';
import { CustomFilter, RecordPager } from '../api/common';
import { toast } from 'react-toastify';

type RecordDisplay<T> = (
  results: T[],
  removeItem: (id: string) => Promise<void>,
  restoreItem: (id: string) => Promise<void>
) => ReactNode;

export const WithRecords = <T extends Record<string, any>>({
  getPage,
  remove,
  restore,
  filters,
  removeKey,
  showDeleted,
  children,
}: {
  children: RecordDisplay<T>;
  getPage: RecordPager<T>;
  remove: (token: string, id: string) => Promise<E.Either<Error, any>>; // we ignore return type
  showDeleted: boolean;
  restore: (token: string, id: string) => Promise<E.Either<Error, any>>; // we ignore return type
  filters?: Record<string, CustomFilter>;
  removeKey?: string;
}) => {
  const [activeCursor, setActiveCursor] = useState<null | string>(null);

  const [cursors, setCursors] = useState<{
    prev: null | string;
    next: null | string;
  }>({ prev: null, next: null });

  const [loading, setLoading] = useState(false);

  const [results, setResults] = useState<T[]>([]);

  const auth = useAuth();

  const token = auth?.token ?? null;

  const removeItem = async (id: string) => {
    if (!token) {
      return;
    }

    const result = await remove(token, id);
    if (E.isLeft(result)) {
      toast.error(`Error removing item: ${result.left.message}`);
    } else if (!showDeleted) {
      const k = removeKey ?? '_id';
      setResults(results.filter((r) => r[k] !== id));
    } else {
      const k = removeKey ?? '_id';
      setResults(
        results.map((r) =>
          r[k] === id ? { ...r, deletedAt: new Date().toISOString() } : r
        )
      );
    }
  };

  const restoreItem = async (id: string) => {
    if (!token || !restore) {
      return;
    }

    const result = await restore(token, id);
    if (E.isLeft(result)) {
      toast.error(`Error restoring item: ${result.left.message}`);
    } else {
      const k = removeKey ?? '_id';
      setResults(
        results.map((r) => (r[k] === id ? { ...r, deletedAt: undefined } : r))
      );
    }
  };

  useEffect(() => {
    if (token) {
      setLoading(true);

      getPage(token, activeCursor, filters)
        .then((result) => {
          if (E.isRight(result)) {
            setCursors({ prev: result.right.prev, next: result.right.next });
            setResults(result.right.data);
          } else {
            const message =
              result.left?.message ??
              result.left?.helpMessage ??
              'Failed to fetch data';
            toast.error(`Error loading results: ${message}`);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          toast.error('Error loading results: Failed to fetch data');
        });
    }
  }, [token, activeCursor, filters, getPage]);

  // If you want the overlay to be contained, ensure the
  //   parent has a position of relative or absolute
  if (loading) {
    return (
      <LoadingOverlay
        visible={true}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
    );
  }

  const { prev, next } = cursors;

  return (
    <Box>
      {children(results, removeItem, restoreItem)}
      <Flex align="center" justify="space-between">
        {prev ? (
          <Button onClick={() => setActiveCursor(prev)}>Previous</Button>
        ) : (
          <Button disabled>Previous</Button>
        )}
        {next ? (
          <Button onClick={() => setActiveCursor(next)}>Next</Button>
        ) : (
          <Button disabled>Next</Button>
        )}
      </Flex>
    </Box>
  );
};
